import api from './api';
import { Pharmacy } from '@/types';
import { apiCache, cacheTTL } from '@/utils/api-cache';

export interface SetActivePharmacyRequest {
  pharmacy_id: string;
}

export interface SetActivePharmacyResponse {
  message: string;
  pharmacy: Pharmacy;
}

export interface VaccineProtocol {
  protocolId: string;
  displayName: string;
  therapyClass: string;
  protocolType: string;
  ageGroup: string;
  category: string;
  eligibility: {
    min_age_years: number | null;
    max_age_years: number | null;
    min_weight_kg: number | null;
    min_weight_lbs: number | null;
    pregnancy: string | null;
    pregnancy_notes: string | null;
    breastfeeding: string | null;
    breastfeeding_notes: string | null;
  };
  contraindications: {
    allergies: string[];
    conditions: string[];
    condition_details: Record<string, string>;
  };
  drugInteractions: {
    contraindicated: string[];
    reduced_efficacy: string[];
    increased_toxicity: string[];
    monitoring_required: string[];
  };
  dosing: {
    adult: {
      dose: string;
      frequency: string;
      route: string;
      instructions: string;
    };
    pediatric: any[];
  };
  timing: {
    start_days_before: number | null;
    days_after_leaving: number | null;
    minimum_days_before_travel: number | null;
    booster_interval: string | null;
  };
  counselingPoints: string[];
  sideEffects: {
    common: string[];
    serious: string[];
    when_to_seek_care: string;
  };
  storage: {
    temperature: string | null;
    special_instructions: string | null;
  };
  notes: string | null;
}

export interface MedicationProtocol {
  protocolId: string;
  displayName: string;
  therapyClass: string;
  protocolType: string;
  ageGroup: string;
  category?: string;
  eligibility: {
    min_age_years: number | null;
    max_age_years: number | null;
    min_weight_kg: number | null;
    min_weight_lbs: number | null;
    pregnancy: string | null;
    pregnancy_notes: string | null;
    breastfeeding: string | null;
    breastfeeding_notes: string | null;
  };
  contraindications: {
    allergies: string[];
    conditions: string[];
    condition_details: Record<string, string>;
  };
  drugInteractions: {
    contraindicated: string[];
    reduced_efficacy: string[];
    increased_toxicity: string[];
    monitoring_required: string[];
  };
  dosing: {
    adult: {
      dose: string;
      frequency: string;
      route: string;
      instructions: string;
    };
    pediatric: any[];
  };
  timing: {
    start_days_before: number | null;
    days_after_leaving: number | null;
    minimum_days_before_travel: number | null;
    booster_interval: string | null;
  };
  counselingPoints: string[];
  sideEffects: {
    common: string[];
    serious: string[];
    when_to_seek_care: string;
  };
  storage: {
    temperature: string | null;
    special_instructions: string | null;
  };
  notes: string | null;
}

export interface ProtocolsResponse {
  message: string;
  filters: {
    type: string | null;
    therapyClass: string | null;
    ageGroup: string | null;
  };
  counts: {
    vaccines: number;
    medications: number;
  };
  protocols: {
    vaccines: VaccineProtocol[];
    medications: MedicationProtocol[];
  };
}

export interface GetProtocolsParams {
  type?: 'vaccine' | 'medication';
  therapy_class?: string;
  age_group?: 'adults' | 'pediatric' | 'pregnancy' | 'all' | Array<'adults' | 'pediatric' | 'pregnancy' | 'all'>;
  search?: string; // Search query for filtering protocols
}

export const pharmacyService = {
  /**
   * Set the active pharmacy for the current user (admin only)
   * Pharmacists are restricted to their assigned pharmacy
   * Invalidates dashboard and forms caches as they are pharmacy-specific
   */
  async setActivePharmacy(pharmacyId: string): Promise<SetActivePharmacyResponse> {
    try {
      const response = await api.post<SetActivePharmacyResponse>(
        '/pharmacy/set-active-pharmacy',
        { pharmacy_id: pharmacyId }
      );
      
      // Store the selected pharmacy ID in localStorage
      if (response.data.pharmacy?.id) {
        localStorage.setItem('currentPharmacyId', response.data.pharmacy.id);
      }
      
      // Invalidate all pharmacy-specific caches
      apiCache.invalidatePattern(/^dashboard/);
      apiCache.invalidatePattern(/^forms:list/);
      
      return response.data;
    } catch (error) {
      console.error('Set active pharmacy error:', error);
      throw error;
    }
  },

  /**
   * Get all available vaccines and medications with optional filters
   * Results are cached for 5 minutes as protocols change infrequently
   * Supports multiple age_group values (e.g., for yellow fever which has age_group='all')
   */
  async getProtocols(params?: GetProtocolsParams): Promise<ProtocolsResponse> {
    // Build query string from params
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.therapy_class) queryParams.append('therapy_class', params.therapy_class);
    if (params?.search) queryParams.append('search', params.search);
    
    // Handle age_group as either string or array
    if (params?.age_group) {
      if (Array.isArray(params.age_group)) {
        params.age_group.forEach(ag => queryParams.append('age_group', ag));
      } else {
        queryParams.append('age_group', params.age_group);
      }
    }
    
    const queryString = queryParams.toString();
    const url = `/pharmacy/protocols${queryString ? `?${queryString}` : ''}`;
    
    // Cache key includes the query params for separate cache entries
    const cacheKey = `protocols:${queryString || 'all'}`;
    
    return apiCache.fetch(
      cacheKey,
      async () => {
        const response = await api.get<ProtocolsResponse>(url);
        return response.data;
      },
      cacheTTL.medium // 5 minutes - protocols don't change frequently
    );
  },
};
